<template>
  <ul
    v-click-outside="onClickOutside"
    class="bg-elev-context-menu-bg border border-bor-nav rounded-12 py-1 text-1316 text-fg-base-fg3 whitespace-nowrap"
  >
    <template
      v-for="item in items"
      :key="item.id"
    >
      <li
        v-if="item.id === 'remove'"
        class="h-[1px] bg-bor-nav my-1"
      />
      <call-session-guest-menu-item
        :item="item"
        @click="onClick(item)"
      />
    </template>
  </ul>
</template>

<script setup lang="ts">
import { ClickOutside as vClickOutside, useRenderNotifier } from '@wui/common-library'
import { useIM } from '@wui/im'
import { computed, ref } from 'vue'


import { Guest } from '@/entities/conference-participant'
import { chatModel } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'
import { useWebRTC } from '@/entities/webrtc'

import { useLocalization } from '@/shared/composables/localization'
import { useAutoAnswer } from '@/shared/composables/use-auto-answer'
import { useRoomControl } from '@/shared/composables/use-room-control'
import { useStoreGuestStatus } from '@/shared/guest-status'
import { Room } from '@/shared/types/store'
import { fromPServedUser } from '@/shared/utils/uri'

import { CallSessionGuestMenuItem as CallSessionGuestMenuItemType } from '..'

import CallSessionGuestMenuItem from './call-session-guest-menu-item.vue'

const props = defineProps<{
  room: Room,
  guest: Guest,
  isPinned: boolean
}>()

const emit = defineEmits<{
  (event: 'togglePin'): void
  (event: 'close'): void
}>()

const { t } = useLocalization()
const { notifyError, notifySuccess } = useRenderNotifier()
const {
  memberCall,
} = useRoomControl()

const { removeSubscriber } = useIM()
const { registered } = useWebRTC()
const { getStatusByPServed } = useStoreGuestStatus()
const isGuestOnline = computed(() => getStatusByPServed(props.guest.pServed)?.isOnline === true)
const storeRooms = useStoreRooms()
const { registerAutoAnswer } = useAutoAnswer()

const handDownInProgress = ref(false)
const isRemoving = ref(false)

const items = computed<CallSessionGuestMenuItemType[]>(() => {
  const menuItems: CallSessionGuestMenuItemType[] = []

  if (isGuestOnline.value) {
    menuItems.push({
      id: 'call',
      label: t('common.connect'),
      icon: registered ? 'phone' : 'callDisabled',
    })
  }

  menuItems.push({
    id: 'pin',
    label: props.isPinned ? t('common.unpin') : t('common.pin'),
    icon: props.isPinned ? 'pinOffM' : 'pinnedM',
  }, {
    id: 'remove',
    label: t('chat.removeFromGroup'),
    icon: handDownInProgress.value ? 'spinner' : 'trashM',
  })

  return menuItems
})

const call = async () => {
  const awaitAutoAnswer = registerAutoAnswer(props.room.pServedUser, true)
  const guestNumber = fromPServedUser(props.guest.pServed)
  await memberCall(props.room.pServedUser, guestNumber)
  return awaitAutoAnswer
}

const remove = async () => {
  if (isRemoving.value) {
    return
  }

  isRemoving.value = true
  removeSubscriber({ pServed: props.room.pServedUser, targetInternalNumber: props.guest.pServed })
    .then((data) => {
      if (data) {
        storeRooms.removeGuest(props.room.id, props.guest.pServed)
        chatModel.useChatMessageStore().addMessage(props.room.pServedUser, data)
        notifySuccess(t('guest.guestNameRemovedFromGroup', { name: props.guest.name }))
      } else {
        notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
      }
    }).catch(() => {
      notifyError(t('guest.failedToRemoveGuestFromRoom', { name: props.guest.name }))
    }).finally(() => {
      isRemoving.value = false
    })
}

const onClick = (item: CallSessionGuestMenuItemType) => {
  if (item.id === 'call') {
    call()
  }

  if (item.id === 'remove') {
    remove()
  }

  if (item.id === 'pin') {
    emit('togglePin')
  }

  emit('close')
}

const onClickOutside = () => {
  emit('close')
}
</script>
